import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import generateUuid from 'uuid/v4'

import { currentOrderUuid } from '../utils/orderNumbers'
import { makeLineItemData } from '../utils/orderUtils'
import { isTabbed } from '../utils/orderStates'

import { getCartItems, getTotal } from '../selectors/cart'
import { getCurrentMenu, getIsKiosk } from '../selectors/menus'
import useRemoteOrderTotal from './useRemoteOrderTotal'
import { getActiveRevenueCenterId } from '../selectors/revenueCenter'
import { restoreQRPaymentandAuthorizationFailedStatus, updateOrderInProgress } from '../actions/order'
import { getAllOrders } from '../selectors/order'
import { useHistory } from 'react-router-dom'
import { calculateServiceCharge } from '../utils/totalUtils'
import { getMenuServiceCharge } from '../selectors/items'

const useCreateOrderInProgress = (orderUuid = null, fromBalanceLookup = false) => {
  const [loadRemoteOrderTotal, { data: remoteOrderTotal, success, failed }] = useRemoteOrderTotal()
  const [orderCreated, setOrderCreated] = useState(false)
  const [tipAmountInCents, setTipAmountInCents] = useState(0)
  const [charityAmountInCents, setCharityAmountInCents] = useState(0)
  const [affiliations, setAffiliations] = useState([])
  const [userUuid, setUserUuid] = useState(null)

  const menu = useSelector(getCurrentMenu)
  const cartItems = useSelector(getCartItems)
  const revenueCenterUuid = useSelector(getActiveRevenueCenterId)
  const offlineTotal = useSelector(getTotal)
  const noPaymentRequired = (remoteOrderTotal?.totalAmountInCents ?? offlineTotal) === 0
  const dispatch = useDispatch()
  const orders = useSelector(getAllOrders)
  const orderFromState = orders?.[orderUuid] || null
  const isKiosk = useSelector(getIsKiosk)
  const serviceCharge = useSelector((state) => getMenuServiceCharge(state))
  const history = useHistory()

  useEffect(() => {
    if (success || failed) {
      const uuid = orderFromState?.uuid ?? remoteOrderTotal?.uuid ?? generateUuid()

      if (fromBalanceLookup) {
        return
      }
      // If somehow arriving here with no items, re-route back to menu
      if (isEmpty(cartItems) && isEmpty(orderFromState?.itemModels)) {
        dispatch(restoreQRPaymentandAuthorizationFailedStatus(uuid))
        if (isKiosk) {
          history.push('/kiosk/order/new')
        } else {
          history.push('/concession-order')
        }
        return
      }

      const total = remoteOrderTotal?.totalAmountInCents ?? offlineTotal

      let serviceFee = { name: 'Service Charge', total: 0, legal: null }
      if (remoteOrderTotal?.totalAmountInCents !== undefined) {
        serviceFee = {
          name: remoteOrderTotal?.serviceChargeName,
          total: remoteOrderTotal?.serviceChargeInCents ?? 0,
          legal: remoteOrderTotal?.serviceChargeLegal,
        }
      } else {
        serviceFee = {
          name: serviceCharge.name,
          total: calculateServiceCharge(serviceCharge, total) ?? 0,
          legal: serviceCharge.legal,
        }
      }

      let order = {
        uuid: orderUuid ?? currentOrderUuid(),
        balanceDueInCents: total + tipAmountInCents + charityAmountInCents,
        payments: [],
        amountInCents: total + tipAmountInCents + charityAmountInCents,
        taxAmountInCents: remoteOrderTotal?.taxAmountInCents ?? 0,
        taxRulesBreakdown: remoteOrderTotal?.taxRulesBreakdown || {},
        subtotalAmountInCents: remoteOrderTotal?.subtotalInCents ?? total,
        discountAmountInCents: remoteOrderTotal?.discountAmountInCents ?? 0,
        serviceFee,
        itemModels: orderFromState?.itemModels ?? makeLineItemData(cartItems),
        menuSubtype: orderFromState?.menuSubtype ?? menu?.subtype,
        revenueCenterUuid,
        charityAmountInCents,
        tipAmountInCents,
        standMenuUuid: menu?.standUuid,
        hasRemoteTotal: remoteOrderTotal?.totalAmountInCents ?? false,
        affiliations,
        userUuid,
        taxExempt: remoteOrderTotal?.taxExempt,
      }

      if (isTabbed(orderFromState)) {
        order = {
          ...orderFromState,
          ...order,
        }
      }
      dispatch(updateOrderInProgress(order))
      setOrderCreated(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, failed])

  const createOrder = (reTotaling, userUuid, affiliations = [], oldTipAmountInCents = 0, oldCharityAmountInCents = 0) => {
    setCharityAmountInCents(oldCharityAmountInCents)
    setTipAmountInCents(oldTipAmountInCents)
    setAffiliations(affiliations)
    setOrderCreated(false)
    setUserUuid(userUuid)

    loadRemoteOrderTotal(reTotaling, affiliations, userUuid)
  }

  return [createOrder, orderCreated, noPaymentRequired, remoteOrderTotal]
}

export default useCreateOrderInProgress
